import React, {useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {
	Paper,
	Box,
	Typography,
	Button,
	Slide,
	IconButton,
	Menu,
	MenuItem,
	Chip,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	CircularProgress,
} from '@material-ui/core';
import {
	Close,
	CheckCircle,
	Cancel,
	LocalShipping,
} from '@material-ui/icons';
import {useOrders} from '../context/OrdersContext';
import useAlert from '../../../../services/useAlert';

const useStyles = makeStyles((theme) => ({
	bulkBar: {
		position: 'fixed',
		bottom: theme.spacing(3),
		left: '50%',
		transform: 'translateX(-50%)',
		zIndex: 1000,
		maxWidth: 800,
		width: '90%',
		padding: theme.spacing(2, 3),
		borderRadius: 12,
		boxShadow: '0 8px 32px rgba(0,0,0,0.16)',
		backgroundColor: theme.palette.primary.main,
		color: '#fff',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: theme.spacing(2),
	},
	selectedCount: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
	},
	actions: {
		display: 'flex',
		gap: theme.spacing(1),
		flexWrap: 'wrap',
	},
	actionButton: {
		backgroundColor: 'rgba(255,255,255,0.2)',
		color: '#fff',
		borderColor: 'rgba(255,255,255,0.5)',
		textTransform: 'none',
		fontWeight: 600,
		'&:hover': {
			backgroundColor: 'rgba(255,255,255,0.3)',
		},
	},
	closeButton: {
		color: '#fff',
	},
	dialogTitle: {
		borderBottom: `1px solid ${theme.palette.divider}`,
	},
}));

const statusOptions = [
	{value: 2, label: 'New', icon: '🆕'},
	{value: 3, label: 'Accepted', icon: '✅'},
	{value: 4, label: 'Packed', icon: '📦'},
	{value: 5, label: 'On The Way', icon: '🚚'},
	{value: 6, label: 'Delivered', icon: '✔️'},
	{value: 7, label: 'Cancelled', icon: '❌'},
	{value: 8, label: 'Arrived', icon: '📍'},
];

const BulkActionsBar = () => {
	const classes = useStyles();
	const {state, actions} = useOrders();
	const {selectedOrderIds} = state;
	const alert = useAlert();

	const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
	const [confirmDialog, setConfirmDialog] = useState({open: false, action: null});
	const [selectedStatus, setSelectedStatus] = useState('');
	const [processing, setProcessing] = useState(false);

	const show = selectedOrderIds.length > 0;

	// Handle status menu
	const handleStatusMenuOpen = (event) => {
		setStatusMenuAnchor(event.currentTarget);
	};

	const handleStatusMenuClose = () => {
		setStatusMenuAnchor(null);
	};

	const handleStatusSelect = (status) => {
		setSelectedStatus(status);
		setConfirmDialog({
			open: true,
			action: 'status',
			title: 'Update Order Status',
			message: `Are you sure you want to update ${selectedOrderIds.length} order(s) to "${statusOptions.find(s => s.value === status)?.label}"?`,
		});
		handleStatusMenuClose();
	};

	// Handle bulk actions
	const handleBulkPickupFile = () => {
		setConfirmDialog({
			open: true,
			action: 'pickup',
			title: 'Send Pickup File',
			message: `Do you want to send pickup file for ${selectedOrderIds.length} selected order(s)?`,
		});
	};

	const handleBulkCancel = () => {
		setSelectedStatus(7); // Cancelled status
		setConfirmDialog({
			open: true,
			action: 'cancel',
			title: 'Cancel Orders',
			message: `Are you sure you want to cancel ${selectedOrderIds.length} order(s)? This action cannot be undone.`,
		});
	};

	const handleBulkAccept = () => {
		setSelectedStatus(3); // Accepted status
		setConfirmDialog({
			open: true,
			action: 'accept',
			title: 'Accept Orders',
			message: `Accept ${selectedOrderIds.length} order(s)?`,
		});
	};

	// Confirm action
	const handleConfirm = async () => {
		setProcessing(true);

		try {
			let result;

			switch (confirmDialog.action) {
				case 'status':
				case 'cancel':
				case 'accept':
					result = await actions.bulkUpdateStatus(selectedOrderIds, selectedStatus);
					break;

				case 'pickup':
					result = await actions.bulkCreatePickupFile(selectedOrderIds);
					break;

				default:
					result = {success: false};
			}

			// Close the dialog first
			setConfirmDialog({open: false, action: null});

			if (result.success) {
				// Show success notification
				alert.success('Success', 'Operation completed successfully!');
				actions.clearSelection();
			} else {
				// Show error notification
				alert.error('Error', result.message || 'Operation failed');
			}
		} catch (error) {
			// Close the dialog
			setConfirmDialog({open: false, action: null});
			alert.error('Error', 'An error occurred: ' + error.message);
		} finally {
			setProcessing(false);
		}
	};

	const handleCloseDialog = () => {
		if (!processing) {
			setConfirmDialog({open: false, action: null});
		}
	};

	return (
		<>
			<Slide direction="up" in={show} mountOnEnter unmountOnExit>
				<Paper className={classes.bulkBar} elevation={8}>
					<Box className={classes.selectedCount}>
						<Chip
							label={selectedOrderIds.length}
							color="secondary"
							size="small"
						/>
						<Typography variant="body1" style={{fontWeight: 600}}>
							{selectedOrderIds.length === 1 ? 'order selected' : 'orders selected'}
						</Typography>
					</Box>

					<Box className={classes.actions}>
						<Button
							variant="outlined"
							size="small"
							startIcon={<CheckCircle/>}
							onClick={handleBulkAccept}
							className={classes.actionButton}
						>
							Accept
						</Button>

						<Button
							variant="outlined"
							size="small"
							onClick={handleStatusMenuOpen}
							className={classes.actionButton}
						>
							Change Status
						</Button>

						<Button
							variant="outlined"
							size="small"
							startIcon={<LocalShipping/>}
							onClick={handleBulkPickupFile}
							className={classes.actionButton}
						>
							Send Pickup File
						</Button>

						<Button
							variant="outlined"
							size="small"
							startIcon={<Cancel/>}
							onClick={handleBulkCancel}
							className={classes.actionButton}
							style={{borderColor: '#ffcdd2', color: '#fff'}}
						>
							Cancel Orders
						</Button>
					</Box>

					<IconButton
						size="small"
						onClick={actions.clearSelection}
						className={classes.closeButton}
					>
						<Close/>
					</IconButton>
				</Paper>
			</Slide>

			{/* Status Menu */}
			<Menu
				anchorEl={statusMenuAnchor}
				open={Boolean(statusMenuAnchor)}
				onClose={handleStatusMenuClose}
			>
				{statusOptions.map((status) => (
					<MenuItem
						key={status.value}
						onClick={() => handleStatusSelect(status.value)}
					>
						<span style={{marginRight: 8}}>{status.icon}</span>
						{status.label}
					</MenuItem>
				))}
			</Menu>

			{/* Confirmation Dialog */}
			<Dialog
				open={confirmDialog.open}
				onClose={handleCloseDialog}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle className={classes.dialogTitle}>
					{confirmDialog.title}
				</DialogTitle>

				<DialogContent style={{paddingTop: 24}}>
					<Typography variant="body1">
						{confirmDialog.message}
					</Typography>
				</DialogContent>

				<DialogActions>
					<Button
						onClick={handleCloseDialog}
						disabled={processing}
					>
						Cancel
					</Button>
					<Button
						onClick={handleConfirm}
						color="primary"
						variant="contained"
						disabled={processing}
						startIcon={processing && <CircularProgress size={20}/>}
					>
						{processing ? 'Processing...' : 'Confirm'}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default BulkActionsBar;
